import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  InputAdornment,
  Paper,
  Typography,
  Divider,
} from '@material-ui/core';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import FormTextField from '../../../components/FormTextField';
import { useCoinUnitPrices, useCoinUnitPricesMutation } from '../../../hooks/queries/useCoinUnitPrice';
import useConfirmDialog from '../../../hooks/useConfirmDialog';
import { CoinUnitPriceType } from '../../../services/api/CoinUnitPrice';
import { ICoinUnitPrice } from '../../../services/types';
import CoinUnitPriceTable from './CoinUnitPriceTable';
import useStyles from './styles';

const CoinUnitPriceToBeFinalizedView = (): JSX.Element => {
  const classes = useStyles();
  const confirm = useConfirmDialog();

  const { data: coinUnitPricesFromQuery } = useCoinUnitPrices(CoinUnitPriceType.ToBeFinalized);
  const { update } = useCoinUnitPricesMutation();

  const [coinUnitPrices, setCoinUnitPrices] = useState<ICoinUnitPrice[]>();
  const [coinUnitPriceToBeConfirmed, setCoinUnitPriceToBeConfirmed] = useState<ICoinUnitPrice>();
  const [finalPriceString, setFinalPriceString] = useState<string>('');

  const isMatchFinalizeCoinUnitPriceConfirm =
    coinUnitPriceToBeConfirmed?.unitPrice.toLocaleString('en-US', { maximumFractionDigits: 5 }) === finalPriceString;

  useEffect(() => {
    if (coinUnitPricesFromQuery) {
      setCoinUnitPrices(coinUnitPricesFromQuery);
    }
  }, [coinUnitPricesFromQuery]);

  const handleCoinUnitPriceChange = (price: ICoinUnitPrice) => {
    setCoinUnitPrices(ps => {
      if (!ps) {
        return;
      }

      const cloned = [...ps];

      const targetIndex = cloned.findIndex(({ id }) => id === price.id);
      cloned[targetIndex] = Object.assign({}, cloned[targetIndex], price);

      return cloned;
    });
  };

  const handleConfirmPopupClose = () => {
    setFinalPriceString('');
    setCoinUnitPriceToBeConfirmed(undefined);
  };

  const handleFinalizeClick = () => {
    if (!coinUnitPriceToBeConfirmed) {
      return;
    }

    handleConfirmPopupClose();

    const onProceed = async () => {
      const result = await update.mutateAsync(coinUnitPriceToBeConfirmed);
      console.log(result);
    };

    confirm.open({
      title: 'Finalize the coin unit price',
      text: 'Once you finalize, you cannot change the coin unit price.',
      onProceed,
    });
  };

  if (coinUnitPrices && coinUnitPrices.length === 0) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" height="calc(100vh - 320px)">
        <Typography className={classes.noToBeFinalizedItemText}>
          You have finished finalizing the coin unit prices for the available months.
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Box>
        {coinUnitPrices &&
          coinUnitPrices.map(coinUnitPrice => {
            const { google, apple, video, unitPrice, coins, radishPlusPool } = coinUnitPrice;
            const { credited, subscription, rewarded_video, offerwall, 'one-time': oneTime, 'radish-plus': radishPlus } = coins;

            const revenue = google + apple + video;
            const totalIssuedPaidCoins = credited + oneTime + subscription + rewarded_video + offerwall + (radishPlus ?? 0);
            const calculatedCoinUnitPrice = Number((revenue - radishPlusPool) / totalIssuedPaidCoins).toLocaleString('en-US', {
              maximumFractionDigits: 5,
            });
            const isNegative = Number(revenue) <= Number(radishPlusPool);

            return (
              <Box key={coinUnitPrice.id} marginBottom="40px">
                <Box display="flex" alignItems="center" marginBottom="12px">
                  <Typography className={classes.titleYear}>{moment(coinUnitPrice.month, 'YYYYMM').format('MMM YYYY')}</Typography>
                  <Typography variant="subtitle1">Coin Unit Price Finalization</Typography>
                </Box>
                <Paper>
                  <CoinUnitPriceTable
                    type={CoinUnitPriceType.ToBeFinalized}
                    coinUnitPrices={[coinUnitPrice]}
                    onRevenueChange={handleCoinUnitPriceChange}
                  />
                  <Box padding="25px">
                    <Box marginBottom="25px">
                      <Typography style={{ paddingBottom: '10px' }}>Calculated Coin Unit Price</Typography>
                      <div className={classes.coinUnitPriceContainer}>
                        <div>(A)</div>
                        <div className={classes.coinUnitPriceValue}>${Number(revenue).toLocaleString()}</div>
                        <div>Total revenue from paid coin</div>
                        <div>(B)</div>
                        <div className={classes.coinUnitPriceValue}>${Number(radishPlusPool).toLocaleString()}</div>
                        <div>Radish PLUS subscription pool (Radish PLUS subscription transaction count * Dedicated subscription price)</div>
                        <div>(C)</div>
                        <div className={classes.coinUnitPriceValue}>{Number(totalIssuedPaidCoins).toLocaleString()}</div>
                        <div>Total paid coin issued</div>
                      </div>
                      <Divider />
                      <div className={classes.coinUnitPriceContainer}>
                        <div>(A-B)&#247;C</div>
                        <div
                          className={classes.coinUnitPriceValue}
                          style={{ fontWeight: 'bold', color: isNegative ? 'red' : 'black' }}
                        >{`$${calculatedCoinUnitPrice}`}</div>
                        <div></div>
                      </div>
                    </Box>
                    <Box>
                      <Typography>Enter the Final Coin Unit Price</Typography>
                      <Box display="flex" marginTop="8px">
                        <FormTextField
                          variant="outlined"
                          defaultValue={Number(unitPrice || calculatedCoinUnitPrice).toLocaleString('en-US', { maximumFractionDigits: 5 })}
                          onValueSubmit={(price: string) => {
                            const cloned = Object.assign({}, coinUnitPrice);
                            cloned.unitPrice = Number(price);

                            handleCoinUnitPriceChange(cloned);
                          }}
                          size="small"
                          style={{ width: '107px', height: '34px', marginRight: '8px' }}
                          InputProps={{
                            startAdornment: <InputAdornment position="start">$</InputAdornment>,
                          }}
                          error={!unitPrice && !calculatedCoinUnitPrice}
                        />
                        <Button
                          variant="outlined"
                          color="primary"
                          onClick={() => {
                            const cloned = Object.assign({}, coinUnitPrice);
                            cloned.unitPrice = Number(unitPrice || calculatedCoinUnitPrice);

                            setCoinUnitPriceToBeConfirmed(cloned);
                          }}
                          disabled={isNegative}
                          style={{ width: 66 }}
                        >
                          Finalize
                        </Button>
                      </Box>
                    </Box>
                  </Box>
                </Paper>
              </Box>
            );
          })}
      </Box>
      <Dialog open={!!coinUnitPriceToBeConfirmed} onClose={handleConfirmPopupClose}>
        <DialogTitle disableTypography style={{ fontSize: '16px', fontWeight: 'bold', paddingBottom: '4px', letterSpacing: 0 }}>
          Confirm the coin unit price
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            <span>{'Please confirm the final coin unit price, '}</span>
            <Typography
              color="primary"
              style={{ display: 'inline', fontWeight: 'bold' }}
              component="span"
            >{`$${coinUnitPriceToBeConfirmed?.unitPrice.toLocaleString('en-US', { maximumFractionDigits: 5 }) || '0'}. `}</Typography>
            <span>{'If it is correct, please enter the final coin unit price again.'}</span>
          </DialogContentText>
          <FormTextField
            variant="outlined"
            value={finalPriceString}
            onValueChange={(value: string) => {
              setFinalPriceString(value);
            }}
            size="small"
            InputProps={{
              style: { width: '120px', height: '34px' },
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
            }}
            FormHelperTextProps={{
              style: { marginLeft: 0 },
            }}
            helperText={!isMatchFinalizeCoinUnitPriceConfirm && finalPriceString.length > 0 ? 'The coin unit price you entered does not match.' : ' '}
            error={!isMatchFinalizeCoinUnitPriceConfirm && finalPriceString.length > 0}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleConfirmPopupClose}>CANCEL</Button>
          <Button onClick={handleFinalizeClick} color="primary" variant="contained" disabled={!isMatchFinalizeCoinUnitPriceConfirm}>
            FINALIZE
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CoinUnitPriceToBeFinalizedView;
